// Billing Health — at-a-glance current-plan card for Home.
// Shows plan name, credits used / included progress bar, projected invoice
// total, and a deeplink to /billing. Self-hides when the user has no billing
// module access (e.g. EntityAdmin/Viewer). Part of iter-79 PAYG Billing.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useSettings } from "@/lib/settings-context";
import { Badge } from "@/components/ui/badge";
import { CreditCard, AlertTriangle, ChevronRight, Zap } from "lucide-react";

export default function BillingHealthWidget({ onEmpty }) {
    const { canAccess } = useSettings();
    const [data, setData] = useState(null);
    const hasAccess = canAccess("billing");

    useEffect(() => {
        if (typeof onEmpty === "function") onEmpty(!hasAccess);
        // eslint-disable-next-line
    }, [hasAccess]);

    useEffect(() => {
        if (!hasAccess) return;
        (async () => {
            try {
                const [s, u] = await Promise.all([
                    api.get("/billing/subscription"),
                    api.get("/billing/usage"),
                ]);
                setData({ sub: s.data, usage: u.data });
            } catch {
                setData({ error: true });
            }
        })();
    }, [hasAccess]);

    if (!hasAccess) {
        return <div className="hidden" data-testid="billing-health-hidden" />;
    }

    if (!data) {
        return (
            <div className="border border-border bg-card rounded-sm p-4 h-full" data-testid="billing-health-loading">
                <div className="eyebrow mb-2">Billing health</div>
                <div className="text-xs text-muted-foreground">Loading…</div>
            </div>
        );
    }

    if (data.error) {
        return (
            <div className="border border-border bg-card rounded-sm p-4 h-full" data-testid="billing-health-error">
                <div className="eyebrow mb-2">Billing health</div>
                <div className="text-xs text-muted-foreground">Unable to load.</div>
            </div>
        );
    }

    const plan = data.sub?.plan;
    const sub = data.sub?.subscription;
    const invoice = data.sub?.invoice;
    const usage = data.usage;
    const included = usage?.credits_included || 0;
    const used = usage?.credits_used || 0;
    const usedPct = included > 0 ? Math.min(100, (used / included) * 100) : 0;
    const over = included > 0 && used > included;
    const projected = invoice?.projected_total_usd || 0;

    return (
        <Link
            to="/billing"
            className="border border-border bg-card rounded-sm p-4 h-full flex flex-col hover:border-primary/50 transition-colors group"
            data-testid="billing-health-widget"
        >
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                    <CreditCard className="h-3.5 w-3.5 text-primary" />
                    <span className="eyebrow text-[10px]">Billing health</span>
                </div>
                <ChevronRight className="h-3 w-3 text-muted-foreground/50 group-hover:text-foreground transition-colors" />
            </div>

            <div className="flex items-baseline gap-2 mb-1">
                <span className="text-sm font-medium" data-testid="billing-health-plan-name">
                    {plan?.name || sub?.plan_slug || "—"}
                </span>
                <Badge variant="outline" className="font-mono text-[9px] uppercase">{sub?.status || "none"}</Badge>
                {sub?.gateway && <Badge variant="secondary" className="font-mono text-[9px] uppercase">{sub.gateway}</Badge>}
            </div>

            {included > 0 && (
                <div className="mt-2">
                    <div className="flex items-center justify-between text-[10px] mb-1">
                        <span className="text-muted-foreground">AI credits</span>
                        <span className="font-mono" data-testid="billing-health-credits">
                            {used.toLocaleString()} / {included.toLocaleString()}
                        </span>
                    </div>
                    <div className="h-1 bg-muted rounded-full overflow-hidden">
                        <div
                            className={`h-full transition-all ${over ? "bg-amber-500" : usedPct > 80 ? "bg-amber-400" : "bg-primary"}`}
                            style={{ width: `${Math.min(100, usedPct)}%` }}
                            data-testid="billing-health-bar"
                        />
                    </div>
                </div>
            )}

            <div className="mt-auto pt-3 flex items-end justify-between">
                <div>
                    <div className="eyebrow text-[9px] mb-0.5">Projected invoice</div>
                    <div className="kpi-number text-xl font-medium" data-testid="billing-health-projected">
                        ${projected.toFixed(2)}
                    </div>
                </div>
                {over && (
                    <div className="flex items-center gap-1 text-[10px] text-amber-600" data-testid="billing-health-overage-flag">
                        <AlertTriangle className="h-3 w-3" />
                        <span>Overage</span>
                    </div>
                )}
                {!over && plan?.base_price_usd > 0 && (
                    <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                        <Zap className="h-3 w-3" />
                        <span>{included > 0 ? `${Math.round(usedPct)}% of included` : "PAYG"}</span>
                    </div>
                )}
            </div>
        </Link>
    );
}
